/** Builders for enemy abilities. Keeps the NPC files declarative. */
import type { Affects, AffixId, AreaShape, EffectSpec, EnemyAbility, MechanicSpec, Ms, School, ZoneEffect } from '@keystone/sim';
import { NPC_AOE_SCALE, NPC_BUSTER_SCALE, NPC_TELEGRAPH_SCALE, npcHit } from '../balance';

/** Everything in the party within this radius of the caster (room-wide pulses). */
export const ROOM_WIDE = 60;

export const hit = (school: School, base: number, extra: Partial<Extract<EffectSpec, { type: 'damage' }>> = {}): EffectSpec => ({
  type: 'damage',
  school,
  amount: npcHit(base),
  ...extra,
});

/** Party-wide pulse centred on the caster. */
export const pulse = (school: School, base: number): EffectSpec => ({
  type: 'damage',
  to: 'area',
  area: { anchor: 'caster', shape: { type: 'circle', radius: ROOM_WIDE }, affects: 'enemies' },
  school,
  amount: npcHit(base),
  avoidable: false,
});

/** Delayed area attack; the hit lands on everyone still inside when it resolves. */
export function telegraph(
  shape: AreaShape,
  anchor: 'caster' | 'target',
  delayMs: Ms,
  effects: EffectSpec[],
  opts: { affects?: Affects; follow?: boolean; repeat?: { intervalMs: Ms; repeats: number } } = {},
): EffectSpec {
  return {
    type: 'telegraph',
    shape,
    anchor,
    delayMs,
    affects: opts.affects ?? 'enemies',
    effects,
    ...(opts.follow ? { follow: true } : {}),
    ...(opts.repeat ? { repeat: opts.repeat } : {}),
  };
}

/** Defaults for an enemy cast. Busters reach a little past melee so kiting a step does not dodge them. */
export function enemy(def: Omit<EnemyAbility, 'targeting'> & { targeting?: EnemyAbility['targeting'] }): EnemyAbility {
  return {
    targeting:
      def.target === 'self' || def.target === 'none'
        ? 'none'
        : def.target === 'lowestHpAlly' || def.target === 'allies'
          ? 'ally'
          : 'enemy',
    range: 40,
    interruptible: false,
    gcdMs: 0,
    ...def,
    effects: scaleEnemyEffects(def.effects, def.kind === 'tankbuster' ? NPC_BUSTER_SCALE : 1, false),
  };
}

/**
 * Balance pass knobs applied to an enemy ability's damage: tank-busters by NPC_BUSTER_SCALE,
 * instant area damage (to: 'area', i.e. pulses) by NPC_AOE_SCALE, dodgeable telegraph/zone
 * damage by NPC_TELEGRAPH_SCALE. Aura periodics are left alone.
 */
function scaleEnemyEffects(effects: readonly EffectSpec[], base: number, inArea: boolean): EffectSpec[] {
  return effects.map((e): EffectSpec => {
    switch (e.type) {
      case 'damage': {
        const k = base * (inArea ? NPC_TELEGRAPH_SCALE : e.to === 'area' ? NPC_AOE_SCALE : 1);
        if (k === 1 || e.amount.base === undefined) return e;
        return { ...e, amount: { ...e.amount, base: Math.round(e.amount.base * k) } };
      }
      case 'conditional':
        return { ...e, then: scaleEnemyEffects(e.then, base, inArea), ...(e.else ? { else: scaleEnemyEffects(e.else, base, inArea) } : {}) };
      case 'telegraph':
        return { ...e, effects: scaleEnemyEffects(e.effects, base, true) };
      case 'zone':
        return e.tick ? { ...e, tick: { ...e.tick, effects: scaleEnemyEffects(e.tick.effects, base, true) } } : e;
      default:
        return e;
    }
  });
}

// ---------------------------------------------------------------------------
// +encounters: mechanic helpers (sim/src/ai/mechanics). Damage inside a mechanic is NOT run
// through the enemy() balance knobs, so these take *effective* numbers.
// ---------------------------------------------------------------------------

/** Wrap a mechanic spec as an effect. */
export const mech = (spec: MechanicSpec): EffectSpec => ({ type: 'mechanic', spec });

/** Key-level gate: `then` at `minKey`+ (and the optional affix), `otherwise` below. */
export const atKey = (minKey: number, then: EffectSpec[], otherwise: EffectSpec[] = []): EffectSpec =>
  mech({ kind: 'gate', minKey, effects: then, elseEffects: otherwise });

/**
 * Failure tax (encounters.md, "Integration tuning"): a dodgeable hit that lands `mult`× harder from
 * `minKey` (default +10). Low keys keep the teaching number; at +10 a missed dodge is near-lethal.
 */
export const keyHit = (
  school: School,
  base: number,
  mult: number,
  extra: Partial<Extract<EffectSpec, { type: 'damage' }>> = {},
  minKey = 10,
): EffectSpec => atKey(minKey, [hit(school, Math.round(base * mult), extra)], [hit(school, base, extra)]);

/** Affix gate. */
export const withAffix = (affix: AffixId, then: EffectSpec[], otherwise: EffectSpec[] = []): EffectSpec =>
  mech({ kind: 'gate', affix, effects: then, elseEffects: otherwise });

/** Unavoidable party-wide hit (effective number; not scaled by NPC_AOE_SCALE). */
export const roomHit = (school: School, eff: number): EffectSpec => ({
  type: 'damage',
  to: 'area',
  area: { anchor: 'caster', shape: { type: 'circle', radius: ROOM_WIDE }, affects: 'enemies' },
  school,
  amount: npcHit(eff),
  avoidable: false,
  isAoE: true,
});

/** Area hit around a point/target/caster (effective number). */
export const areaHit = (school: School, eff: number, radius: number, anchor: 'caster' | 'target' | 'point' = 'point'): EffectSpec => ({
  type: 'damage',
  to: 'area',
  area: { anchor, shape: { type: 'circle', radius }, affects: 'enemies' },
  school,
  amount: npcHit(eff),
  avoidable: false,
  isAoE: true,
});

/** Ground zone that ticks damage on occupants (effective per-tick number). */
export function pool(
  zoneId: string,
  radius: number,
  durationMs: Ms,
  tickEff: number,
  opts: { anchor?: 'caster' | 'target' | 'point'; intervalMs?: Ms; extra?: EffectSpec[]; shape?: AreaShape } = {},
): ZoneEffect {
  return {
    type: 'zone',
    zoneId,
    shape: opts.shape ?? { type: 'circle', radius },
    anchor: opts.anchor ?? 'point',
    durationMs,
    affects: 'enemies',
    uniquePerCaster: false,
    tick: { intervalMs: opts.intervalMs ?? 1000, effects: [hit('nature', tickEff, { avoidable: false, isAoE: true }), ...(opts.extra ?? [])] },
  };
}
